<template>
  <div>
    <slot />
    <div v-if="tagline" class="mb-16 font-semibold tracking-wide">
      {{ tagline }}
    </div>
    <h3 class="typo-h2 font-semibold tracking-wide text-blue-900">
      {{ title }}
    </h3>
    <div v-if="allJobTitles.length" class="mt-8 font-semibold tracking-wide">
      <div v-for="jobTitle in allJobTitles" :key="jobTitle">{{ jobTitle }}</div>
    </div>
    <div class="mt-24">
      <div v-if="yearOfBirth">
        <div>
          {{ $texts('yearOfBirth') }}:&nbsp;{{ yearOfBirth }}
        </div>
      </div>
      <template v-if="availability">
        <template v-if="typeof availability === 'string'">
          <div>
            {{ $texts('availabilityFrom') }}:&nbsp;{{
              availability
            }}
          </div>
        </template>
        <template v-else>
          <div>
            {{ $texts('availability') }}:&nbsp;
            <span
              v-for="(item, index) in availability"
              :key="item.long"
              :title="item.long"
              >{{ item.short
              }}<template v-if="index < availability.length - 1"
                >,&nbsp;</template
              ></span
            >
          </div>
        </template>
      </template>
      <div class="mt-24">
        <div v-if="phone?.first">
          <div>
            {{ $texts('phone') }}:&nbsp;
            <FieldItemTypeTelephone
              v-bind="phone.first"
              :title="getLinkTitle($texts('callPerson'))"
              class="inline-link"
            />
          </div>
        </div>
        <div v-if="email">
          <div>
            {{ $texts('email') }}:&nbsp;
            <a
              :href="getEmailLink(email)"
              :title="
                getLinkTitle(
                  $texts('sendEmailToPerson'),
                )
              "
              class="inline-link"
              >{{ email }}</a
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { nonNullable } from '#vuepal/helpers/type'
import type { WeekdayItem } from '~/types/global'
import type { PersonCardFragment } from '#graphql-operations'
import { removeWhitespace } from '~/helpers'

function abbreviateWeekday(day: string, language: string) {
  if (language === 'fr' || language === 'it') {
    return day.substring(0, 3).toLowerCase()
  }
  return day.substring(0, 2)
}

const language = useCurrentLanguage()
const { $texts } = useNuxtApp()

const props = defineProps<{
  id?: string
  familyName?: string
  givenName?: string
  jobTitles?: PersonCardFragment['jobTitles']
  weekdayAvailability?: PersonCardFragment['weekdayAvailability']
  email?: PersonCardFragment['email']
  phone?: PersonCardFragment['phone']
  yearOfBirth?: PersonCardFragment['yearOfBirth']
  image?: PersonCardFragment['image']
  tagline?: string
}>()

const title = computed(() =>
  [props.givenName, props.familyName].filter(Boolean).join(' '),
)

const allJobTitles = computed<string[]>(
  () => props.jobTitles?.map((v) => v?.title).filter(nonNullable) || [],
)

const availability = computed<WeekdayItem[] | string | undefined>(() => {
  if (!props.weekdayAvailability) {
    return
  }

  const entireWeek =
    props.weekdayAvailability.includes('monday') &&
    props.weekdayAvailability.includes('friday') &&
    props.weekdayAvailability.length === 5

  if (entireWeek) {
    return [getWeekdayLabel('monday'), getWeekdayLabel('friday')].join(' - ')
  }

  if (props.weekdayAvailability.length) {
    return (props.weekdayAvailability || [])
      .filter((v): v is string => v !== null && v !== undefined)
      .map((v) => {
        const long = getWeekdayLabel(v)
        return { long, short: abbreviateWeekday(long, language.value) }
      })
  }

  return undefined
})

function getEmailLink(email: string) {
  return 'mailto:' + removeWhitespace(email)
}

function getWeekdayLabel(key: string): string {
  switch (key) {
    case 'monday':
      return $texts('monday')
    case 'tuesday':
      return $texts('tuesday')
    case 'wednesday':
      return $texts('wednesday')
    case 'thursday':
      return $texts('thursday')
    case 'friday':
      return $texts('friday')
    case 'saturday':
      return $texts('saturday')
    case 'sunday':
      return $texts('sunday')
  }

  return key
}
function getLinkTitle(translation = ''): string {
  return translation.replace('@person', title.value || '')
}
</script>
